import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, Send, Archive, RefreshCw, Plus, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import MessageDeliveryStatus from '@/components/messages/MessageDeliveryStatus';
import { matchesFreeText } from '@/components/search/SearchBuilder';

function convTitle(c, meId) {
  if (c?.title) return c.title;
  if (c?.display_title) return c.display_title;
  const others = (c?.participants || []).filter((p) => p.id !== meId);
  if (others.length) return others.map((p) => p.full_name || p.username).join(', ');
  return 'Chat';
}

export default function NachrichtenTab() {
  const { user: me } = useAuth();
  const [tab, setTab] = useState('inbox');
  const [convs, setConvs] = useState([]);
  const [sel, setSel] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState('');
  const [q, setQ] = useState('');
  const [newOpen, setNewOpen] = useState(false);
  const bottomRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/messages/conversations', { params: { include_archived: true } });
      setConvs(r.data || []);
    } catch (e) { toast.error('Nachrichten konnten nicht geladen werden'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openConv = async (c) => {
    setSel(c);
    try {
      const r = await api.get(`/messages/conversations/${c.id}/messages`);
      setMsgs(r.data || []);
      const incoming = (r.data || []).filter((m) => m.sender?.id !== me?.id && !m.deleted_at);
      await Promise.all(incoming.map(async (message) => {
        await api.post(`/messages/messages/${message.id}/delivered`).catch(() => {});
        await api.post(`/messages/messages/${message.id}/read`).catch(() => {});
      }));
      setTimeout(() => bottomRef.current?.scrollIntoView(), 100);
    } catch (e) { toast.error('Chat konnte nicht geladen werden'); }
  };

  const send = async () => {
    if (!text.trim() || !sel) return;
    try {
      const fd = new FormData();
      fd.append('body', text);
      await api.post(`/messages/conversations/${sel.id}/messages`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setText('');
      openConv(sel);
      load();
    } catch (e) { toast.error('Senden fehlgeschlagen'); }
  };

  const archive = async () => {
    if (!sel) { toast.error('Bitte zuerst einen Chat wählen'); return; }
    try {
      await api.post(`/messages/conversations/${sel.id}/archive`);
      toast.success('Archiviert');
      setSel(null);
      setMsgs([]);
      setTab('archive');
      load();
    } catch (e) { toast.error('Archivieren fehlgeschlagen'); }
  };

  const removeChat = async () => {
    if (!sel) { toast.error('Bitte zuerst einen Chat wählen'); return; }
    if (!window.confirm('Chat aus deiner Liste löschen? Der Chat verschwindet nur für dich.')) return;
    try {
      await api.delete(`/messages/conversations/${sel.id}`);
      toast.success('Chat gelöscht');
      setSel(null);
      setMsgs([]);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Löschen fehlgeschlagen'); }
  };

  const deleteMessage = async (msgId) => {
    if (!window.confirm('Nachricht für alle löschen?')) return;
    try {
      await api.delete(`/messages/messages/${msgId}`);
      toast.success('Nachricht gelöscht');
      if (sel) openConv(sel);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Löschen fehlgeschlagen'); }
  };

  const filtered = convs.filter((c) => (tab === 'archive' ? c.archived : !c.archived))
    .filter((c) => matchesFreeText({ ...c, title: convTitle(c, me?.id) }, q));

  const fmt = (d) => d ? new Date(d).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <div className="h-full min-h-0 flex overflow-hidden" data-testid="admin-nachrichten">
      <aside className="w-96 shrink-0 border-r border-slate-200 bg-white flex flex-col min-h-0 h-full sticky top-0 self-start">
        <div className="p-3 space-y-2 border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-1.5 text-xs">
            <button onClick={() => setTab('inbox')} data-testid="admin-nachrichten-tab-inbox"
                    className={`px-3 py-1.5 rounded-full font-medium ${tab === 'inbox' ? 'bg-brand-red text-white' : 'bg-slate-100 text-slate-600'}`}>Postfach</button>
            <button onClick={() => { setTab('archive'); setSel(null); }} data-testid="admin-nachrichten-tab-archive"
                    className={`px-3 py-1.5 rounded-full font-medium ${tab === 'archive' ? 'bg-brand-red text-white' : 'bg-slate-100 text-slate-600'}`}>Archiv</button>
            <button onClick={() => setNewOpen(true)} className="ml-auto p-1.5 text-brand-red" data-testid="admin-nachrichten-new"
                    title="Neue Nachricht"><Plus className="w-4 h-4" /></button>
            <button onClick={load} className="p-1.5 text-slate-400 hover:text-slate-600" data-testid="admin-nachrichten-refresh"
                    title="Aktualisieren"><RefreshCw className="w-4 h-4" /></button>
            <button onClick={archive} disabled={!sel || sel.archived} className="p-1.5 text-slate-400 hover:text-slate-600 disabled:opacity-30"
                    data-testid="admin-nachrichten-archive" title="Archivieren"><Archive className="w-4 h-4" /></button>
            <button onClick={removeChat} disabled={!sel} className="p-1.5 text-slate-400 hover:text-rose-600 disabled:opacity-30"
                    data-testid="admin-nachrichten-delete" title="Chat löschen"><Trash2 className="w-4 h-4" /></button>
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <Input className="pl-9 h-9" placeholder="Schnellsuche" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-nachrichten-search" />
          </div>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto">
          {filtered.map((c) => (
            <button key={c.id} onClick={() => openConv(c)}
                    data-testid={`admin-conv-${c.id}`}
                    className={`w-full text-left px-3 py-2.5 border-b border-slate-100 ${sel?.id === c.id ? 'bg-brand-red/5' : 'hover:bg-slate-50'}`}>
              <span className="flex justify-between">
                <span className="text-sm font-medium truncate">{convTitle(c, me?.id)}</span>
                {c.unread_count > 0 && <span className="text-[10px] font-bold px-1.5 rounded-full bg-brand-red text-white">{c.unread_count}</span>}
              </span>
              <span className="flex justify-between text-xs text-slate-400">
                <span className="truncate">{c.last_message_preview || '—'}</span>
                <span className="shrink-0 ml-2">{fmt(c.last_message_at)}</span>
              </span>
            </button>
          ))}
          {!filtered.length && <p className="p-6 text-center text-sm text-slate-400">{tab === 'inbox' ? 'Keine Chats — starte mit +' : 'Archiv leer'}</p>}
        </div>
      </aside>

      <div className="flex-1 min-h-0 flex flex-col bg-slate-50 overflow-hidden">
        {sel ? (
          <>
            <div className="flex items-center justify-between px-5 py-3 bg-white border-b border-slate-200 shrink-0">
              <h2 className="font-semibold text-sm truncate">{convTitle(sel, me?.id)}</h2>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-2">
              {msgs.map((m) => {
                const isOwn = m.sender?.id === me?.id;
                const deleted = !!m.deleted_at;
                const canDelete = !deleted && (isOwn || me?.is_super_admin);
                return (
                  <div key={m.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`} data-testid={`admin-msg-${m.id}`}>
                    <div className={`max-w-[70%] rounded-xl px-3.5 py-2 text-sm ${isOwn ? 'bg-brand-red text-white' : 'bg-white border border-slate-200'}`}>
                      {!isOwn && <p className="text-[11px] font-semibold text-brand-red mb-0.5">{m.sender?.full_name || m.sender?.username}</p>}
                      <p className={`whitespace-pre-wrap ${deleted ? 'italic opacity-70' : ''}`}>
                        {deleted ? 'Nachricht gelöscht' : m.body}
                      </p>
                      <p className={`text-[10px] mt-1 flex items-center justify-end gap-1.5 ${isOwn ? 'text-white/80' : 'text-slate-400'}`}>
                        <span>{fmt(m.created_at)}</span>
                        {isOwn && !deleted && <MessageDeliveryStatus message={m} />}
                        {canDelete && (
                          <button type="button" onClick={() => deleteMessage(m.id)}
                                  className={`p-0.5 rounded hover:opacity-100 opacity-70 ${isOwn ? 'hover:bg-brand-red/90' : 'hover:bg-slate-100 hover:text-rose-600'}`}
                                  title="Nachricht löschen" data-testid={`admin-msg-delete-${m.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>
            <div className="p-3 bg-white border-t border-slate-200 flex gap-2 shrink-0">
              <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Antwort schreiben…"
                     onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); send(); } }} data-testid="admin-nachrichten-input" />
              <Button onClick={send} className="bg-brand-red hover:bg-brand-red/90" data-testid="admin-nachrichten-send"><Send className="w-4 h-4" /></Button>
            </div>
          </>
        ) : (
          <p className="m-auto text-slate-400 text-sm">Chat links auswählen oder + für eine neue Nachricht</p>
        )}
      </div>

      {newOpen && (
        <NewChatDialog
          onClose={() => setNewOpen(false)}
          onCreated={(conv) => { setNewOpen(false); setTab('inbox'); load(); openConv(conv); }}
        />
      )}
    </div>
  );
}

function NewChatDialog({ onClose, onCreated }) {
  const { user: me } = useAuth();
  const [q, setQ] = useState('');
  const [people, setPeople] = useState([]);
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loadErr, setLoadErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(() => {
      api.get('/messages/directory', { params: { q: q.trim() || undefined, limit: 50 } })
        .then((r) => {
          if (cancelled) return;
          setLoadErr('');
          setPeople((r.data || []).map((u) => ({
            id: u.id,
            full_name: u.full_name,
            email: u.email || u.username,
            tenant_name: u.tenant_name,
          })));
        })
        .catch((e) => {
          if (!cancelled) setLoadErr(e?.response?.data?.detail || 'Empfänger konnten nicht geladen werden');
        });
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  const selfMatch = !!(q.trim() && me && matchesFreeText(me, q));

  const start = async () => {
    if (!picked) { toast.error('Bitte eine Person wählen'); return; }
    setBusy(true);
    try {
      const r = await api.post('/messages/conversations', {
        kind: 'direct', participant_ids: [picked.id],
      });
      onCreated(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Chat konnte nicht gestartet werden');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="admin-nachrichten-new-dialog">
        <DialogHeader>
          <DialogTitle>Neue Nachricht</DialogTitle>
        </DialogHeader>
        <Input placeholder="Person suchen (Name oder E-Mail)…" value={q}
               onChange={(e) => setQ(e.target.value)} onInput={(e) => setQ(e.target.value)}
               data-testid="admin-nachrichten-new-search" autoComplete="off" />
        <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
          {loadErr && <p className="p-3 text-xs text-rose-600">{loadErr}</p>}
          {!loadErr && selfMatch && !people.length && (
            <p className="p-3 text-xs text-slate-500" data-testid="admin-nachrichten-self-hint">
              Das bist du selbst — ein Chat mit dem eigenen Konto ist nicht möglich.
            </p>
          )}
          {!loadErr && !selfMatch && !people.length && (
            <p className="p-3 text-xs text-slate-400">
              {qn ? 'Keine Treffer.' : 'Tippe einen Namen oder eine E-Mail.'}
            </p>
          )}
          {people.map((u) => (
            <button key={u.id} onClick={() => setPicked(u)}
                    data-testid={`admin-nachrichten-pick-${u.id}`}
                    className={`w-full text-left px-3 py-2 text-sm ${picked?.id === u.id ? 'bg-brand-red/5' : 'hover:bg-slate-50'}`}>
              <p className="font-medium">{u.full_name || u.email}</p>
              <p className="text-xs text-slate-500">{u.email}{u.tenant_name ? ` · ${u.tenant_name}` : ''}</p>
            </button>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={start} disabled={busy || !picked} className="bg-brand-red hover:bg-brand-red/90"
                  data-testid="admin-nachrichten-new-start">Chat starten</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
